// Shared presentation helpers for the Enquiries pages (Session ARE, D82): the status words and
// the draft-tier label, and two small pure formatters. Mirrors complaintsUi.tsx's
// role for the Complaints pages, so EnquiriesPage.tsx and EnquiryDetailPage.tsx cannot drift on how
// a status or a counterparty reads.
import type { EnquiryDraftTier, EnquiryFactType, EnquiryListRow, EnquiryStatus } from "../../lib/api";
import { draftSourceLabel } from "./emailEngineUi";

// Plain-English, never the raw status key (house rule: assume zero platform-jargon knowledge on
// anything the office manager touches).
export const ENQUIRY_STATUS_LABEL: Record<EnquiryStatus, string> = {
  new: "New",
  drafted: "Drafted",
  awaiting_review: "Awaiting your review",
  approved: "Approved",
  sent: "Sent",
};

// Session AUA (email system Phase 3): where the current draft's wording came from, worded exactly as the Email engine page
// words it (one home: draftSourceLabel). "Free draft" or "From template <key>, version N"; null when nothing says.
export function enquiryDraftSource(e: Pick<EnquiryListRow, "draftKind" | "draftTemplateKey" | "draftTemplateVersion">): string | null {
  return draftSourceLabel({ kind: e.draftKind, templateKey: e.draftTemplateKey, templateVersion: e.draftTemplateVersion });
}

export const DRAFT_TIER_LABEL: Record<EnquiryDraftTier, string> = {
  templated: "Drafted by the email engine, passed every check",
  novel: "Needs your own reply",
};

// Who this is from, as the office would read it: a matched name is shown with its address when
// both are known, falling back to whichever one the mailbox actually carried, and never a blank
// string when the sender is genuinely unknown.
export function counterpartyLine(e: Pick<EnquiryListRow, "counterpartyName" | "counterpartyEmail">): string {
  if (e.counterpartyName && e.counterpartyEmail) return `${e.counterpartyName} <${e.counterpartyEmail}>`;
  return e.counterpartyName || e.counterpartyEmail || "Unknown sender";
}

// Gmail's own web permalink for a thread (mirrors InboxTriagePage.tsx's un-exported helper of the
// same name and reasoning: #all/ rather than #inbox/ since the underlying sweep covers the whole
// mailbox, and u/0 is the same default account slot every such link in this app already assumes).
export function gmailThreadHref(threadId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;
}

// Session ATM, Part D: plain-English label for each of the three fact types the office can
// approve or dismiss, never the raw enum key.
export const FACT_TYPE_LABEL: Record<EnquiryFactType, string> = {
  contact_change: "New or changed contact",
  site_access_change: "Site access change",
  price_confirmation: "Price or invoicing note",
};

// A one-line summary of an HTML draft body for the bulk-approve list: tags stripped, collapsed to
// a single line, then truncated. Never used for anything the office relies on instead of reading
// the full draft -- it is a glance only, which is why the bulk-approve UI still requires the full
// body to have been opened before a row counts as read (Session ATM, Part C).
export function draftBodySummary(html: string | null | undefined, max = 160): string {
  if (!html) return "";
  const text = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}...`;
}
